import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import type { ServiceTypeOption, LegalEntitySelectItem, AddPhoneResponse, PhoneQuotaInfo } from "../types";
import OveragePlanModal from "./OveragePlanModal";

const ROW_COUNT = 11;

interface PhoneRow {
  phone: string;
  label: string;
  serviceType: string;
  legalEntityId: string;
}

function createEmptyRow(defaultLegalEntityId: string): PhoneRow {
  return { phone: "", label: "", serviceType: "", legalEntityId: defaultLegalEntityId };
}

interface AddPhonesSectionProps {
  serviceTypes: ServiceTypeOption[];
  legalEntities: LegalEntitySelectItem[];
  isSysadmin: boolean;
  allowMultipleLegalEntities: boolean;
  onAddPhones: (
    phones: { Phone: string; Label: string; ServiceType: number; TrustCallLegalEntity: number | null }[]
  ) => Promise<AddPhoneResponse>;
  isSubmitting: boolean;
  quota?: PhoneQuotaInfo;
  isOnlySysadminAllowedToAddTCNumber?: boolean;
  isDemo?: boolean;
  remainingCredits?: number;
}

// Check overage for each service type in priority order (matching MVC: scanning → remediation → daily scans).
// Returns the first exceeded plan type's overage count and label, or null if under quota.
function checkOverage(
  quota: PhoneQuotaInfo,
  phones: { ServiceType: number }[]
): { overage: number; planLabel: string } | null {
  const scanningCount = phones.filter((p) => p.ServiceType === 1).length;
  if (scanningCount > 0) {
    const overage = (quota.currentMonitoredCount + scanningCount) - quota.monitoredPlanLimit;
    if (overage > 0) return { overage, planLabel: "scanning plan subscription" };
  }

  const remediationCount = phones.filter((p) => p.ServiceType === 2).length;
  if (remediationCount > 0) {
    const overage = (quota.currentRemediationCount + remediationCount) - quota.remediationPlanLimit;
    if (overage > 0) return { overage, planLabel: "remediation plan subscription" };
  }

  const dailyScanCount = phones.filter((p) => p.ServiceType === 3).length;
  if (dailyScanCount > 0) {
    const overage = (quota.currentRemediationDailyScanCount + dailyScanCount) - quota.remediationDailyScanPlanLimit;
    if (overage > 0) return { overage, planLabel: "remediation daily scans subscription" };
  }

  return null;
}

const AddPhonesSection: React.FC<AddPhonesSectionProps> = ({
  serviceTypes,
  legalEntities,
  isSysadmin,
  allowMultipleLegalEntities,
  onAddPhones,
  isSubmitting,
  quota,
  isOnlySysadminAllowedToAddTCNumber = true,
  isDemo = false,
  remainingCredits = ROW_COUNT,
}) => {
  const navigate = useNavigate();

  const visibleServiceTypes = serviceTypes.filter(
    (st) => st.isEnabled && (!st.isSysadminOnly || isSysadmin)
  );

  const defaultLegalEntityId = legalEntities.find((le) => le.isDefault)?.legalEntityId?.toString() ?? "";

  // MVC line 5660-5703: demo accounts limit row count to min(remainingCredits, 11)
  const initialRowCount = isDemo ? Math.min(remainingCredits, ROW_COUNT) : ROW_COUNT;

  const [rows, setRows] = useState<PhoneRow[]>(() =>
    Array.from({ length: initialRowCount }, () => createEmptyRow(defaultLegalEntityId))
  );
  const [pendingPhones, setPendingPhones] = useState<{ Phone: string; Label: string; ServiceType: number; TrustCallLegalEntity: number | null }[] | null>(null);
  const [overageCount, setOverageCount] = useState(0);
  const [overagePlanLabel, setOveragePlanLabel] = useState("");
  const [phoneErrors, setPhoneErrors] = useState<{ phoneNumber: string; status: string }[]>([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [scanningConfirm, setScanningConfirm] = useState<{ rowIndex: number; prevValue: string } | null>(null);

  const updateRow = useCallback((index: number, field: keyof PhoneRow, value: string) => {
    // MVC line 5780-5794: input filtering
    let filtered = value;
    if (field === "phone") {
      // Digits only (MVC: replace(/[^\d].+/, ""))
      filtered = value.replace(/[^\d]/g, "");
    } else if (field === "label") {
      // Alphanumeric, spaces, hyphens only (MVC: /^[A-Za-z0-9- ]+$/)
      filtered = value.replace(/[^A-Za-z0-9 -]/g, "");
    }
    setRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: filtered };
      return updated;
    });
  }, []);

  // MVC line 3851-3860: when service type changes to Scanning (1), show confirmation modal
  const handleServiceTypeChange = useCallback((index: number, value: string) => {
    if (value === "1") {
      // Store the previous value so we can revert on "No"
      setScanningConfirm({ rowIndex: index, prevValue: rows[index].serviceType });
      // Optimistically set to Scanning — "No" will revert
      updateRow(index, "serviceType", value);
    } else {
      updateRow(index, "serviceType", value);
    }
  }, [rows, updateRow]);

  const submitPhones = async (phones: { Phone: string; Label: string; ServiceType: number; TrustCallLegalEntity: number | null }[]) => {
    try {
      const result = await onAddPhones(phones);

      // Authorization/credits check (matches MVC line 5576-5578)
      if (result?.notAuthorized) {
        toast.error("You do not have sufficient credits to perform this action or demo credits already consumed. Please contact your sales rep.");
        return;
      }

      if (result?.phoneStatusDTO ?? result) {
        const dto = result.phoneStatusDTO ?? result;
        const successCount = dto.phoneCountSuccessfullyAdded ?? 0;
        const errors = dto.phoneErrorList ?? [];

        if (errors.length > 0) {
          // MVC line 5582-5583: only show success toast if phoneCountSucessfullyAdded > 0
          if (successCount > 0) {
            toast.success(`Successfully added ${successCount} phone number(s)`, { duration: 5000 });
          }
          // MVC line 5585-5591: show error modal with list of failed phones
          setPhoneErrors(errors);
          setShowErrorModal(true);
        } else {
          // MVC line 5596-5600: full success — toast + navigate to Current Numbers (refresh grid)
          toast.success(`Successfully added ${successCount} phone number(s)`, { duration: 5000 });
          navigate("/trustcall/phones");
        }

        // MVC line 5603-5610: always clear and regenerate form rows after response
        // (regardless of errors — MVC clears form in all cases when phoneStatusDTO exists)
        const rowCount = result.isDemo && result.remainingCredits != null
          ? Math.min(result.remainingCredits, ROW_COUNT)
          : ROW_COUNT;
        setRows(Array.from({ length: rowCount }, () => createEmptyRow(defaultLegalEntityId)));
      }
    } catch {
      // MVC line 5797-5799: fail handler
      toast.error("Error adding phone number");
    }
  };

  const handleSubmit = async () => {
    // MVC line 5447-5450, 5538-5541: sysadmin-only add check
    if (!isOnlySysadminAllowedToAddTCNumber) {
      toast.info("Only systems admin are allowed to add phone numbers");
      return;
    }

    // Collect non-empty rows — MVC line 5546-5561: skip rows where phone is empty
    const filledRows = rows.filter((r) => r.phone.trim() !== "");

    // MVC line 5562-5565: if no phones, silently return (MVC has commented-out toastr)
    if (filledRows.length === 0) {
      toast.info("Please add Phone Number to monitor");
      return;
    }

    // MVC line 5554-5555: default serviceType to 1, always send legalEntityId (MVC always has a value)
    const phones = filledRows.map((r) => ({
      Phone: r.phone.trim(),
      Label: r.label.trim(),
      ServiceType: r.serviceType ? parseInt(r.serviceType, 10) : 1,
      TrustCallLegalEntity: r.legalEntityId
        ? parseInt(r.legalEntityId, 10)
        : (defaultLegalEntityId ? parseInt(defaultLegalEntityId, 10) : null),
    }));

    // Quota overage check (matches MVC DoNotAllowTCOverage client-side pre-check)
    if (quota) {
      const result = checkOverage(quota, phones);
      if (result) {
        setPendingPhones(phones);
        setOverageCount(result.overage);
        setOveragePlanLabel(result.planLabel);
        return;
      }
    }

    await submitPhones(phones);
  };

  return (
    <div>
    <div className="card mb-3">
      {/* Header */}
      <div className="card-header tc-card-header">
        <h5 className="mb-0 text-white">Add Phone Numbers</h5>
      </div>

      <div className="card-body">
        {/* MVC demoCreditsWarning: shown when demo credits exhausted */}
        {isDemo && remainingCredits <= 0 && (
          <div className="alert alert-warning" role="alert" style={{ marginTop: 10, marginBottom: 10 }}>
            <strong>Demo credits already consumed. Please contact your sales rep to sign up.</strong>
          </div>
        )}

        <p className="text-muted mb-3">
          To add multiple numbers place each number on a separate line
        </p>

        {/* Column headers — plain text, left-aligned, no background */}
        <div className="row fw-bold mb-1">
          <div className={allowMultipleLegalEntities ? "col-3" : "col-4"}>Phone</div>
          <div className={allowMultipleLegalEntities ? "col-3" : "col-4"}>Label</div>
          <div className={allowMultipleLegalEntities ? "col-3" : "col-4"}>Service Type</div>
          {allowMultipleLegalEntities && <div className="col-3">Legal Entity</div>}
        </div>

        {/* Input rows — no borders, just form controls */}
        {rows.map((row, i) => (
          <div className="row mb-1" key={i}>
            <div className={allowMultipleLegalEntities ? "col-3" : "col-4"}>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Phone Number"
                maxLength={11}
                value={row.phone}
                onChange={(e) => updateRow(i, "phone", e.target.value)}
              />
            </div>
            <div className={allowMultipleLegalEntities ? "col-3" : "col-4"}>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Sales, Customer Service, etc."
                value={row.label}
                onChange={(e) => updateRow(i, "label", e.target.value)}
              />
            </div>
            <div className={allowMultipleLegalEntities ? "col-3" : "col-4"}>
              <select
                className="form-select form-select-sm"
                value={row.serviceType}
                onChange={(e) => handleServiceTypeChange(i, e.target.value)}
              >
                <option value="">--Service Type--</option>
                {visibleServiceTypes.map((st) => (
                  <option key={st.id} value={st.id}>
                    {st.isSysadminOnly ? `${st.name} (Sysadmin Only)` : st.name}
                  </option>
                ))}
              </select>
            </div>
            {allowMultipleLegalEntities && (
              <div className="col-3">
                <select
                  className="form-select form-select-sm"
                  value={row.legalEntityId}
                  onChange={(e) => updateRow(i, "legalEntityId", e.target.value)}
                >
                  {legalEntities.map((le) => (
                    <option key={le.legalEntityId} value={le.legalEntityId}>
                      {le.legalName}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>

    {/* Add button — outside card, matches MVC: separate row below */}
    <div className="d-grid gap-2" style={{ marginTop: 10 }}>
      <button
        className="btn btn-lg"
        style={{ backgroundColor: "var(--navbar-and-treeview-bg)", borderColor: "var(--navbar-and-treeview-bg)", color: "#fff" }}
        onClick={handleSubmit}
        disabled={isSubmitting}
      >
        {isSubmitting ? "Adding..." : "Add"}
      </button>
    </div>

      <OveragePlanModal
        show={!!pendingPhones}
        overageCount={overageCount}
        planLabel={overagePlanLabel}
        onConfirm={async () => {
          if (pendingPhones) {
            await submitPhones(pendingPhones);
          }
          setPendingPhones(null);
        }}
        onCancel={() => setPendingPhones(null)}
      />

      {/* Phone Error Modal — matches MVC #phoneModal */}
      {showErrorModal && (
        <div className="modal d-block" tabIndex={-1}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: "#ff0000", padding: "0.75rem 1rem" }}>
                <h5 className="modal-title" style={{ color: "#fff" }}>Phone number(s) not added to monitoring list</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowErrorModal(false)} />
              </div>
              <div className="modal-body">
                {phoneErrors.map((err, i) => (
                  <p key={i} style={{ borderBottom: "1px solid #dee2e6", paddingBottom: 8, marginBottom: 8 }}>
                    Phone number &quot;{err.phoneNumber}&quot; is {err.status}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scanning Confirmation Modal — MVC #remediationModal */}
      {scanningConfirm && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.3)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h3 className="modal-title">Confirmation</h3>
                <button type="button" className="btn-close" onClick={() => {
                  updateRow(scanningConfirm.rowIndex, "serviceType", scanningConfirm.prevValue);
                  setScanningConfirm(null);
                }} />
              </div>
              <div className="modal-body">
                <span>Are you sure you want to set this phone number to monitor only?</span>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    // MVC line 4095-4098: revert to first option (empty)
                    updateRow(scanningConfirm.rowIndex, "serviceType", scanningConfirm.prevValue);
                    setScanningConfirm(null);
                  }}
                >
                  No
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => setScanningConfirm(null)}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddPhonesSection;
